import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Path, Lock, LockOpen, CheckCircle, Truck } from "@phosphor-icons/react";

const STATUS_STYLE = {
  draft: "bg-[#E5E2D9] text-[#5C5A55]",
  assigned: "bg-[#BBDEFB] text-[#0D47A1]",
  in_progress: "bg-[#F9E4B7] text-[#7A5A11]",
  completed: "bg-[#2D5A27] text-white",
  cancelled: "bg-[#F4C7B9] text-[#7A2E18]",
};

export default function RouteJobsList() {
  const [jobs, setJobs] = useState([]);
  const [date, setDate] = useState("");
  const [sp, setSp] = useSearchParams();
  const status = sp.get("status") || "";
  const setStatus = (v) => {
    if (v) sp.set("status", v); else sp.delete("status");
    setSp(sp, { replace: true });
  };
  const nav = useNavigate();

  const load = async () => {
    try {
      const p = new URLSearchParams();
      if (date) p.set("date", date);
      if (status) p.set("status", status);
      const r = await api.get(`/routes/jobs?${p}`);
      setJobs(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date, status]);

  const title = status === "draft" ? "Optimized Routes"
               : status === "assigned" ? "Route Jobs"
               : "Route Schedule";
  const subtitle = status === "draft" ? "Optimized but not yet assigned"
                   : status === "assigned" ? "Assigned & in-progress jobs"
                   : "All route plans";

  return (
    <AppShell title={title} subtitle={subtitle}
      actions={
        <div className="flex gap-2">
          <button onClick={() => nav("/operations/routes/ai-suggest")}
                  data-testid="btn-ai-suggest-route"
                  className="inline-flex items-center gap-2 h-10 px-4 bg-[#C25D38] hover:bg-[#A54C2C] text-white rounded-md text-sm font-medium">
            <Plus size={14} weight="bold"/>AI Auto-Suggest
          </button>
          <button onClick={() => nav("/operations/routes/new")} data-testid="btn-new-route"
                  className="inline-flex items-center gap-2 h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium">
            <Plus size={14} weight="bold"/>Create Route Plan
          </button>
        </div>
      }>
      <div className="flex gap-3 mb-4">
        <input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="route-filter-date"
               className="h-10 px-3 border border-[#E5E2D9] rounded-md text-sm bg-white"/>
        <select value={status} onChange={e => setStatus(e.target.value)} data-testid="route-filter-status"
                className="h-10 px-3 border border-[#E5E2D9] rounded-md text-sm bg-white">
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="assigned">Assigned</option>
          <option value="in_progress">In progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <div className="ml-auto text-xs text-[#5C5A55] self-center">{jobs.length} job{jobs.length !== 1 ? "s" : ""}</div>
      </div>

      {jobs.length === 0 ? (
        <div className="bg-white border border-[#E5E2D9] rounded-md p-10 text-center text-sm text-[#5C5A55]">
          <Path size={32} className="mx-auto mb-3 opacity-40"/>
          No route jobs yet. Create your first plan to get started.
        </div>
      ) : (
        <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden" data-testid="route-jobs-table">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F2EC] text-[11px] uppercase tracking-[0.18em] text-[#5C5A55]">
              <tr>
                <th className="text-left p-3">Job No</th>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Stops</th>
                <th className="text-left p-3">Distance</th>
                <th className="text-left p-3">Engine</th>
                <th className="text-left p-3">Staff / Vehicle</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3"></th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(j => (
                <tr key={j.id} className="border-t border-[#F0ECE3] hover:bg-[#FAF8F2]" data-testid={`route-job-${j.id}`}>
                  <td className="p-3 font-mono text-xs">{j.job_no}</td>
                  <td className="p-3">{j.name}</td>
                  <td className="p-3 text-[#5C5A55]">{j.scheduled_date}</td>
                  <td className="p-3">{j.stop_count}</td>
                  <td className="p-3 text-[#5C5A55]">{j.distance_km?.toFixed?.(1) || "—"} km</td>
                  <td className="p-3 text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">{j.engine}</td>
                  <td className="p-3 text-xs">
                    {j.assigned_staff_name ? (
                      <div className="flex items-center gap-1.5"><Truck size={12}/>{j.assigned_staff_name}{j.assigned_vehicle_no ? ` · ${j.assigned_vehicle_no}` : ""}</div>
                    ) : <span className="text-[#8A8780]">—</span>}
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${STATUS_STYLE[j.status] || "bg-[#E5E2D9]"}`}>
                      {j.locked ? <Lock size={10} className="inline mr-1"/> : <LockOpen size={10} className="inline mr-1"/>}
                      {j.status.replace("_", " ")}
                    </span>
                  </td>
                  <td className="p-3">
                    <Link to={`/operations/routes/${j.id}`} data-testid={`open-route-${j.id}`}
                          className="text-[#2D5A27] text-xs hover:underline">open</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AppShell>
  );
}
